import React from "react";

// ─────────────────────────────────────────────────────────────────
// STYLE SYSTEM — identical to Vectors page
// ─────────────────────────────────────────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

// ─────────────────────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────────
function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Definition / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Arrow({ x1, y1, x2, y2, color, width, dash, id }) {
  const markerId = "arr_vs_" + (id || `${Math.round(x1)}_${Math.round(y1)}_${Math.round(x2)}_${Math.round(y2)}`);
  return (
    <g>
      <defs>
        <marker id={markerId} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <polygon points="0 0,7 3.5,0 7" fill={color || "#2E75B6"}/>
        </marker>
      </defs>
      <line x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={color || "#2E75B6"} strokeWidth={width || 2.4}
        strokeDasharray={dash || ""} markerEnd={`url(#${markerId})`}/>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — What Makes a Vector Space
// Three panels: R², a valid subspace (line), and a non-subspace (line not through origin)
// ─────────────────────────────────────────────────────────────────
function DiagramVectorSpace() {
  const W = 880, H = 320;

  // Panel positions — three equal panels with padding
  const panels = [
    { x: 10,   label: "R²  (the full plane)", sub: "Valid vector space", ok: true  },
    { x: 310,  label: "Line through origin",  sub: "Valid subspace of R²", ok: true  },
    { x: 610,  label: "Line NOT through origin", sub: "Not a subspace", ok: false },
  ];
  const pw = 260, ph = 260, cx = 130, cy = 130, sc = 44;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Vector Space vs. Subspace — Closure and the Origin Requirement</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {panels.map((p, pi) => {
          const ox = p.x + cx;
          const oy = cy + 20;
          const isSubLine  = pi === 1;
          const isBadLine  = pi === 2;
          const isFullPlane = pi === 0;

          return (
            <g key={pi}>
              {/* panel border */}
              <rect x={p.x} y={8} width={pw} height={ph} rx={6}
                fill="#fff" stroke={p.ok ? "#2E75B6" : "#c0392b"} strokeWidth="1.8"/>

              {/* title */}
              <text x={p.x + pw/2} y={26} textAnchor="middle" fontSize="13"
                fill={p.ok ? "#1F3864" : "#c0392b"} fontWeight="700">{p.label}</text>
              <text x={p.x + pw/2} y={42} textAnchor="middle" fontSize="11.5"
                fill={p.ok ? "#27ae60" : "#c0392b"}>{p.sub}</text>

              {/* grid lines */}
              {[-2,-1,0,1,2].map(v => (
                <g key={v}>
                  <line x1={p.x+8} y1={oy - v*sc} x2={p.x+pw-8} y2={oy - v*sc}
                    stroke="#eee" strokeWidth="1"/>
                  <line x1={ox + v*sc} y1={52} x2={ox + v*sc} y2={oy + 2.2*sc}
                    stroke="#eee" strokeWidth="1"/>
                </g>
              ))}

              {/* axes */}
              <line x1={p.x+8} y1={oy} x2={p.x+pw-8} y2={oy} stroke="#bbb" strokeWidth="1.2"/>
              <line x1={ox} y1={52} x2={ox} y2={oy+2.2*sc} stroke="#bbb" strokeWidth="1.2"/>

              {/* PANEL 0: shaded plane + two vectors */}
              {isFullPlane && (
                <g>
                  <rect x={p.x+10} y={54} width={pw-20} height={ph-62}
                    fill="#EBF3FB" opacity="0.5" rx={3}/>
                  <Arrow x1={ox} y1={oy} x2={ox+1.8*sc} y2={oy-1*sc}
                    color="#2E75B6" width={2.5} id="vs_v1"/>
                  <text x={ox+1.8*sc+8} y={oy-1*sc+4} fontSize="13" fill="#2E75B6" fontWeight="700">v</text>
                  <Arrow x1={ox} y1={oy} x2={ox+0.5*sc} y2={oy-2*sc}
                    color="#27ae60" width={2.5} id="vs_v2"/>
                  <text x={ox+0.5*sc+6} y={oy-2*sc+4} fontSize="13" fill="#27ae60" fontWeight="700">w</text>
                  <Arrow x1={ox} y1={oy} x2={ox+2.3*sc} y2={oy-3*sc}
                    color="#e07b00" width={2} dash="5,3" id="vs_sum"/>
                  <text x={ox+2.3*sc+6} y={oy-3*sc+4} fontSize="12" fill="#e07b00">v+w</text>
                  <text x={p.x+pw/2} y={ph-4} textAnchor="middle" fontSize="11" fill="#555">
                    Closed under + and scalar ×
                  </text>
                </g>
              )}

              {/* PANEL 1: line through origin — valid subspace */}
              {isSubLine && (
                <g>
                  {/* the line extends through origin */}
                  <line x1={p.x+8} y1={oy+2.1*sc} x2={p.x+pw-8} y2={oy-2.1*sc}
                    stroke="#2E75B6" strokeWidth="1.8" strokeDasharray="6,3" opacity="0.4"/>
                  <Arrow x1={ox} y1={oy} x2={ox+1.4*sc} y2={oy-1.4*sc}
                    color="#2E75B6" width={2.5} id="sub_v1"/>
                  <text x={ox+1.4*sc+7} y={oy-1.4*sc+4} fontSize="13" fill="#2E75B6" fontWeight="700">v</text>
                  <Arrow x1={ox} y1={oy} x2={ox-sc} y2={oy+sc}
                    color="#27ae60" width={2.5} id="sub_v2"/>
                  <text x={ox-sc-18} y={oy+sc+4} fontSize="13" fill="#27ae60" fontWeight="700">-v</text>
                  <circle cx={ox} cy={oy} r={5} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
                  <text x={ox+8} y={oy+16} fontSize="11.5" fill="#e07b00" fontWeight="700">origin ✓</text>
                  <text x={p.x+pw/2} y={ph-4} textAnchor="middle" fontSize="11" fill="#555">
                    All scalar multiples stay on the line
                  </text>
                </g>
              )}

              {/* PANEL 2: line NOT through origin — invalid */}
              {isBadLine && (
                <g>
                  {/* shifted line — does not pass through origin */}
                  <line x1={p.x+8} y1={oy-0.5*sc} x2={p.x+pw-8} y2={oy-0.5*sc+2.0*sc*((pw-16)/(pw-16))*0.9}
                    stroke="#c0392b" strokeWidth="1.8" strokeDasharray="6,3" opacity="0.5"/>
                  {/* simpler: diagonal line offset upward */}
                  <line x1={p.x+10} y1={oy-1.8*sc} x2={p.x+pw-10} y2={oy-0.2*sc}
                    stroke="#c0392b" strokeWidth="2" strokeDasharray="6,3" opacity="0.6"/>
                  {/* vector on the line */}
                  <Arrow x1={ox-sc} y1={oy-1.35*sc} x2={ox+0.6*sc} y2={oy-0.78*sc}
                    color="#c0392b" width={2.5} id="bad_v1"/>
                  <text x={ox-sc-4} y={oy-1.35*sc-8} fontSize="13" fill="#c0392b" fontWeight="700">v</text>
                  {/* show origin is NOT on the line */}
                  <circle cx={ox} cy={oy} r={5} fill="#888" stroke="#fff" strokeWidth="1.5"/>
                  <text x={ox+8} y={oy+16} fontSize="11.5" fill="#888">origin ✗</text>
                  {/* 2v arrow pointing off the line visually */}
                  <Arrow x1={ox-sc} y1={oy-1.35*sc} x2={ox+2.2*sc} y2={oy-0.2*sc}
                    color="#9b59b6" width={2} dash="4,3" id="bad_2v"/>
                  <text x={ox+2.2*sc+5} y={oy-0.2*sc+4} fontSize="12" fill="#9b59b6">2v ✗</text>
                  <text x={p.x+pw/2} y={ph-4} textAnchor="middle" fontSize="11" fill="#c0392b">
                    Scalar × leaves the set
                  </text>
                </g>
              )}

              {/* ok/fail badge */}
              <text x={p.x+pw-12} y={26} textAnchor="end" fontSize="16"
                fill={p.ok ? "#27ae60" : "#c0392b"}>{p.ok ? "✓" : "✗"}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        A vector space must contain the zero vector and be closed — adding or scaling any vectors must stay inside the set. The full plane R² is a vector space. A line through the origin is a valid subspace. A line that misses the origin fails because scaling a vector on it can leave the set.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Linear Independence vs Dependence
// Two panels side by side: independent vectors, dependent vectors
// ─────────────────────────────────────────────────────────────────
function DiagramLinearIndependence() {
  const W = 880, H = 300;
  const sc = 52;

  // Left panel: two independent vectors in R²
  const L = { ox: 160, oy: 155 };
  // Right panel: two dependent vectors (one is scalar multiple of other)
  const R = { ox: 610, oy: 155 };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linear Independence — Can One Vector Be Built From the Others?</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT PANEL: Independent ── */}
        <rect x={10} y={8} width={400} height={272} rx={6}
          fill="#fff" stroke="#27ae60" strokeWidth="1.8"/>
        <text x={210} y={26} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">
          Linearly Independent
        </text>
        <text x={210} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          Neither vector is a multiple of the other
        </text>

        {/* grid */}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={18} y1={L.oy+v*sc} x2={402} y2={L.oy+v*sc} stroke="#eee" strokeWidth="1"/>
            <line x1={L.ox+v*sc} y1={52} x2={L.ox+v*sc} y2={270} stroke="#eee" strokeWidth="1"/>
          </g>
        ))}
        <line x1={18} y1={L.oy} x2={402} y2={L.oy} stroke="#bbb" strokeWidth="1.2"/>
        <line x1={L.ox} y1={52} x2={L.ox} y2={270} stroke="#bbb" strokeWidth="1.2"/>

        {/* v1 = (2,1) */}
        <Arrow x1={L.ox} y1={L.oy} x2={L.ox+2*sc} y2={L.oy-1*sc}
          color="#2E75B6" width={2.8} id="ind_v1"/>
        <text x={L.ox+2*sc+8} y={L.oy-1*sc+5} fontSize="14" fill="#2E75B6" fontWeight="700">v₁=(2,1)</text>

        {/* v2 = (0,2) */}
        <Arrow x1={L.ox} y1={L.oy} x2={L.ox+0*sc} y2={L.oy-2*sc}
          color="#27ae60" width={2.8} id="ind_v2"/>
        <text x={L.ox+8} y={L.oy-2*sc-6} fontSize="14" fill="#27ae60" fontWeight="700">v₂=(0,2)</text>

        {/* label: span covers the whole plane */}
        <rect x={18} y={232} width={384} height={38} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={210} y={248} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Span{"{v₁, v₂}"} = all of R²
        </text>
        <text x={210} y={264} textAnchor="middle" fontSize="11" fill="#555">
          Only solution to c₁v₁ + c₂v₂ = 0  is  c₁=c₂=0
        </text>

        {/* ── RIGHT PANEL: Dependent ── */}
        <rect x={460} y={8} width={410} height={272} rx={6}
          fill="#fff" stroke="#c0392b" strokeWidth="1.8"/>
        <text x={665} y={26} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">
          Linearly Dependent
        </text>
        <text x={665} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          One vector is a scalar multiple of the other
        </text>

        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={468} y1={R.oy+v*sc} x2={862} y2={R.oy+v*sc} stroke="#eee" strokeWidth="1"/>
            <line x1={R.ox+v*sc} y1={52} x2={R.ox+v*sc} y2={270} stroke="#eee" strokeWidth="1"/>
          </g>
        ))}
        <line x1={468} y1={R.oy} x2={862} y2={R.oy} stroke="#bbb" strokeWidth="1.2"/>
        <line x1={R.ox} y1={52} x2={R.ox} y2={270} stroke="#bbb" strokeWidth="1.2"/>

        {/* v1 = (1,1) */}
        <Arrow x1={R.ox} y1={R.oy} x2={R.ox+1*sc} y2={R.oy-1*sc}
          color="#2E75B6" width={2.8} id="dep_v1"/>
        <text x={R.ox+1*sc+8} y={R.oy-1*sc+5} fontSize="14" fill="#2E75B6" fontWeight="700">v₁=(1,1)</text>

        {/* v2 = (2,2) = 2·v1 */}
        <Arrow x1={R.ox} y1={R.oy} x2={R.ox+2*sc} y2={R.oy-2*sc}
          color="#c0392b" width={2.8} id="dep_v2"/>
        <text x={R.ox+2*sc+8} y={R.oy-2*sc+5} fontSize="14" fill="#c0392b" fontWeight="700">v₂=(2,2)</text>

        {/* dashed line showing they lie on the same line */}
        <line x1={R.ox-2*sc} y1={R.oy+2*sc} x2={R.ox+2.5*sc} y2={R.oy-2.5*sc}
          stroke="#888" strokeWidth="1.2" strokeDasharray="5,4"/>

        {/* label: v2 = 2·v1 */}
        <rect x={468} y={232} width={394} height={38} rx={4} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={665} y={248} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">
          v₂ = 2·v₁  →  span only covers a line, not R²
        </text>
        <text x={665} y={264} textAnchor="middle" fontSize="11" fill="#555">
          c₁v₁ + c₂v₂ = 0  has non-trivial solutions
        </text>
      </svg>
      <div style={S.dCaption}>
        Independent vectors point in genuinely different directions and together can reach every point in their span. Dependent vectors are redundant — one is already built from the others, so they only span a lower-dimensional subspace.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Span
// Shows how combinations of vectors fill space
// Two stacked panels: span of 1 vector (line), span of 2 vectors (plane)
// ─────────────────────────────────────────────────────────────────
function DiagramSpan() {
  const W = 880, H = 300;
  const sc = 48;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Span — The Set of All Vectors Reachable by Linear Combinations</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: span of one vector = a line ── */}
        <rect x={10} y={8} width={410} height={276} rx={6}
          fill="#fff" stroke="#2E75B6" strokeWidth="1.8"/>
        <text x={215} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Span of One Vector  →  a Line
        </text>
        <text x={215} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          span{"{v}"} = {"{c·v  |  c ∈ ℝ}"}
        </text>

        {/* grid */}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={18} y1={155+v*sc} x2={412} y2={155+v*sc} stroke="#eee" strokeWidth="1"/>
            <line x1={170+v*sc} y1={52} x2={170+v*sc} y2={275} stroke="#eee" strokeWidth="1"/>
          </g>
        ))}
        <line x1={18} y1={155} x2={412} y2={155} stroke="#bbb" strokeWidth="1.2"/>
        <line x1={170} y1={52} x2={170} y2={275} stroke="#bbb" strokeWidth="1.2"/>

        {/* the line through the origin in direction v=(1,1) */}
        <line x1={18} y1={275} x2={378} y2={20}
          stroke="#2E75B6" strokeWidth="2.5" opacity="0.25"/>

        {/* v itself */}
        <Arrow x1={170} y1={155} x2={170+sc} y2={155-sc}
          color="#2E75B6" width={3} id="sp1_v"/>
        <text x={170+sc+8} y={155-sc+5} fontSize="14" fill="#2E75B6" fontWeight="700">v</text>

        {/* 2v */}
        <Arrow x1={170} y1={155} x2={170+2*sc} y2={155-2*sc}
          color="#27ae60" width={2} dash="5,3" id="sp1_2v"/>
        <text x={170+2*sc+6} y={155-2*sc+5} fontSize="13" fill="#27ae60">2v</text>

        {/* -v */}
        <Arrow x1={170} y1={155} x2={170-sc} y2={155+sc}
          color="#c0392b" width={2} dash="5,3" id="sp1_neg"/>
        <text x={170-sc-24} y={155+sc+5} fontSize="13" fill="#c0392b">-v</text>

        <circle cx={170} cy={155} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>

        <rect x={18} y={240} width={394} height={28} rx={3} fill="#EBF3FB"/>
        <text x={215} y={258} textAnchor="middle" fontSize="12" fill="#1F3864">
          1 vector → 1D span (line) — only one direction reachable
        </text>

        {/* ── RIGHT: span of two independent vectors = the plane ── */}
        <rect x={460} y={8} width={410} height={276} rx={6}
          fill="#fff" stroke="#27ae60" strokeWidth="1.8"/>
        <text x={665} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Span of Two Independent Vectors  →  a Plane
        </text>
        <text x={665} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          {"span{v,w} = {c₁·v + c₂·w  |  c₁,c₂ ∈ ℝ}"}
        </text>

        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={468} y1={155+v*sc} x2={862} y2={155+v*sc} stroke="#eee" strokeWidth="1"/>
            <line x1={620+v*sc} y1={52} x2={620+v*sc} y2={275} stroke="#eee" strokeWidth="1"/>
          </g>
        ))}
        <line x1={468} y1={155} x2={862} y2={155} stroke="#bbb" strokeWidth="1.2"/>
        <line x1={620} y1={52} x2={620} y2={275} stroke="#bbb" strokeWidth="1.2"/>

        {/* shaded reachable region */}
        <rect x={470} y={54} width={384} height={180} fill="#EBF3FB" opacity="0.45" rx={3}/>

        {/* v = (1.5, 0.5) */}
        <Arrow x1={620} y1={155} x2={620+1.5*sc} y2={155-0.5*sc}
          color="#2E75B6" width={3} id="sp2_v"/>
        <text x={620+1.5*sc+8} y={155-0.5*sc+5} fontSize="14" fill="#2E75B6" fontWeight="700">v</text>

        {/* w = (0, 1.5) */}
        <Arrow x1={620} y1={155} x2={620} y2={155-1.5*sc}
          color="#27ae60" width={3} id="sp2_w"/>
        <text x={620+8} y={155-1.5*sc-5} fontSize="14" fill="#27ae60" fontWeight="700">w</text>

        {/* a combination c1v+c2w */}
        <Arrow x1={620} y1={155} x2={620+1.5*sc} y2={155-2*sc}
          color="#e07b00" width={2} dash="5,3" id="sp2_combo"/>
        <text x={620+1.5*sc+6} y={155-2*sc-4} fontSize="12" fill="#e07b00">c₁v+c₂w</text>

        <circle cx={620} cy={155} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>

        <rect x={468} y={240} width={394} height={28} rx={3} fill="#f0fff4"/>
        <text x={665} y={258} textAnchor="middle" fontSize="12" fill="#1F3864">
          2 independent vectors → 2D span (whole plane in R²)
        </text>
      </svg>
      <div style={S.dCaption}>
        Span is the complete set of destinations reachable by scaling and adding your vectors. One vector spans a line. Two independent vectors in R² span the entire plane. The span always passes through the origin.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Basis and Dimension
// Shows standard basis vs a non-standard basis, both spanning R²
// ─────────────────────────────────────────────────────────────────
function DiagramBasisDimension() {
  const W = 880, H = 310;
  const sc = 50;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Basis and Dimension — The Minimal Spanning Set</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: Standard basis ── */}
        <rect x={10} y={8} width={410} height={282} rx={6}
          fill="#fff" stroke="#2E75B6" strokeWidth="1.8"/>
        <text x={215} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Standard Basis of R²
        </text>
        <text x={215} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          {"e₁=(1,0)  and  e₂=(0,1)"}
        </text>

        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={18} y1={160+v*sc} x2={412} y2={160+v*sc} stroke="#eee" strokeWidth="1"/>
            <line x1={180+v*sc} y1={52} x2={180+v*sc} y2={280} stroke="#eee" strokeWidth="1"/>
          </g>
        ))}
        <line x1={18} y1={160} x2={412} y2={160} stroke="#bbb" strokeWidth="1.5"/>
        <line x1={180} y1={52} x2={180} y2={280} stroke="#bbb" strokeWidth="1.5"/>

        {/* e1 */}
        <Arrow x1={180} y1={160} x2={180+sc-4} y2={160}
          color="#2E75B6" width={3} id="std_e1"/>
        <text x={180+sc+6} y={164} fontSize="14" fill="#2E75B6" fontWeight="700">e₁</text>

        {/* e2 */}
        <Arrow x1={180} y1={160} x2={180} y2={160-sc+4}
          color="#27ae60" width={3} id="std_e2"/>
        <text x={185} y={160-sc-6} fontSize="14" fill="#27ae60" fontWeight="700">e₂</text>

        {/* example: express (2,1.5) in standard basis */}
        <Arrow x1={180} y1={160} x2={180+2*sc} y2={160-1.5*sc}
          color="#e07b00" width={2.2} dash="4,3" id="std_pt"/>
        <circle cx={180+2*sc} cy={160-1.5*sc} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
        <text x={180+2*sc+8} y={160-1.5*sc+5} fontSize="12" fill="#e07b00">(2, 1.5)</text>

        <rect x={18} y={248} width={394} height={32} rx={3} fill="#EBF3FB"/>
        <text x={215} y={261} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          (2, 1.5) = 2·e₁ + 1.5·e₂
        </text>
        <text x={215} y={275} textAnchor="middle" fontSize="11" fill="#555">
          Coordinates are the scalar coefficients in the basis
        </text>

        {/* ── RIGHT: Non-standard basis ── */}
        <rect x={460} y={8} width={410} height={282} rx={6}
          fill="#fff" stroke="#9b59b6" strokeWidth="1.8"/>
        <text x={665} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Non-Standard Basis of R²
        </text>
        <text x={665} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          {"b₁=(1,1)  and  b₂=(1,-1)  — still spans R²"}
        </text>

        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={468} y1={160+v*sc} x2={862} y2={160+v*sc} stroke="#eee" strokeWidth="1"/>
            <line x1={630+v*sc} y1={52} x2={630+v*sc} y2={280} stroke="#eee" strokeWidth="1"/>
          </g>
        ))}
        <line x1={468} y1={160} x2={862} y2={160} stroke="#bbb" strokeWidth="1.5"/>
        <line x1={630} y1={52} x2={630} y2={280} stroke="#bbb" strokeWidth="1.5"/>

        {/* b1 = (1,1) */}
        <Arrow x1={630} y1={160} x2={630+sc} y2={160-sc}
          color="#9b59b6" width={3} id="ns_b1"/>
        <text x={630+sc+8} y={160-sc+5} fontSize="14" fill="#9b59b6" fontWeight="700">b₁</text>

        {/* b2 = (1,-1) */}
        <Arrow x1={630} y1={160} x2={630+sc} y2={160+sc}
          color="#c0392b" width={3} id="ns_b2"/>
        <text x={630+sc+8} y={160+sc+5} fontSize="14" fill="#c0392b" fontWeight="700">b₂</text>

        {/* same point (2,1.5) in this basis */}
        <Arrow x1={630} y1={160} x2={630+2*sc} y2={160-1.5*sc}
          color="#e07b00" width={2.2} dash="4,3" id="ns_pt"/>
        <circle cx={630+2*sc} cy={160-1.5*sc} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
        <text x={630+2*sc+8} y={160-1.5*sc+5} fontSize="12" fill="#e07b00">(2, 1.5)</text>

        <rect x={468} y={248} width={394} height={32} rx={3} fill="#f5eeff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={665} y={261} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Same point, different coordinates in this basis
        </text>
        <text x={665} y={275} textAnchor="middle" fontSize="11" fill="#555">
          dim(R²) = 2  regardless of which basis you choose
        </text>
      </svg>
      <div style={S.dCaption}>
        A basis is any set of linearly independent vectors that spans the space. The standard basis uses axis-aligned unit vectors, but infinitely many valid bases exist. Dimension is the number of vectors in any basis — it is a fixed property of the space itself, not of the basis chosen.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Subspaces
// Three examples: {0}, a line, the full plane
// ─────────────────────────────────────────────────────────────────
function DiagramSubspaces() {
  const W = 880, H = 290;
  const sc = 46;

  const panels = [
    { x: 10,  label: "Zero Subspace {0}", sub: "dim = 0", color: "#888" },
    { x: 310, label: "Line through origin", sub: "dim = 1", color: "#2E75B6" },
    { x: 610, label: "Full plane R²", sub: "dim = 2", color: "#27ae60" },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Subspaces of R² — Three Examples by Dimension</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {panels.map((p, pi) => {
          const pw = 260, ph = 258;
          const ox = p.x + 130, oy = 148;

          return (
            <g key={pi}>
              <rect x={p.x} y={8} width={pw} height={ph} rx={6}
                fill="#fff" stroke={p.color} strokeWidth="1.8"/>
              <text x={p.x+pw/2} y={26} textAnchor="middle" fontSize="13"
                fill={p.color} fontWeight="700">{p.label}</text>
              <text x={p.x+pw/2} y={42} textAnchor="middle" fontSize="12"
                fill="#555">{p.sub}</text>

              {/* grid */}
              {[-2,-1,0,1,2].map(v => (
                <g key={v}>
                  <line x1={p.x+6} y1={oy+v*sc} x2={p.x+pw-6} y2={oy+v*sc}
                    stroke="#f0f0f0" strokeWidth="1"/>
                  <line x1={ox+v*sc} y1={50} x2={ox+v*sc} y2={ph-6}
                    stroke="#f0f0f0" strokeWidth="1"/>
                </g>
              ))}
              <line x1={p.x+6} y1={oy} x2={p.x+pw-6} y2={oy} stroke="#ccc" strokeWidth="1.2"/>
              <line x1={ox} y1={50} x2={ox} y2={ph-6} stroke="#ccc" strokeWidth="1.2"/>

              {/* PANEL 0: just the origin dot */}
              {pi === 0 && (
                <g>
                  <circle cx={ox} cy={oy} r={7} fill="#888" stroke="#fff" strokeWidth="2"/>
                  <text x={ox} y={oy+22} textAnchor="middle" fontSize="13" fill="#888" fontWeight="700">0</text>
                  <text x={p.x+pw/2} y={ph-10} textAnchor="middle" fontSize="11" fill="#555">
                    Contains only the zero vector
                  </text>
                </g>
              )}

              {/* PANEL 1: a line through origin */}
              {pi === 1 && (
                <g>
                  <line x1={p.x+8} y1={oy+2*sc} x2={p.x+pw-8} y2={oy-2*sc}
                    stroke="#2E75B6" strokeWidth="2.5" opacity="0.35"/>
                  <Arrow x1={ox} y1={oy} x2={ox+sc} y2={oy-sc}
                    color="#2E75B6" width={2.5} id="sub1_v"/>
                  <Arrow x1={ox} y1={oy} x2={ox-sc} y2={oy+sc}
                    color="#2E75B6" width={2} dash="5,3" id="sub1_neg"/>
                  <circle cx={ox} cy={oy} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
                  <text x={p.x+pw/2} y={ph-10} textAnchor="middle" fontSize="11" fill="#555">
                    Infinite points, one direction
                  </text>
                </g>
              )}

              {/* PANEL 2: shaded full plane */}
              {pi === 2 && (
                <g>
                  <rect x={p.x+8} y={52} width={pw-16} height={ph-62}
                    fill="#EBF3FB" opacity="0.55" rx={3}/>
                  <Arrow x1={ox} y1={oy} x2={ox+sc} y2={oy}
                    color="#27ae60" width={2.5} id="sub2_e1"/>
                  <Arrow x1={ox} y1={oy} x2={ox} y2={oy-sc}
                    color="#2E75B6" width={2.5} id="sub2_e2"/>
                  <circle cx={ox} cy={oy} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
                  <text x={p.x+pw/2} y={ph-10} textAnchor="middle" fontSize="11" fill="#555">
                    Every point in the plane reachable
                  </text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Every subspace must contain the origin and be closed under addition and scaling. In R² the only subspaces are the zero subspace, lines through the origin, and the full plane — one for each dimension 0, 1, and 2.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function VectorSpaces() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Vector Spaces</h1>

      <p style={S.p}>
        The Vectors page in Geometry covers what vectors are and how to operate on them geometrically — magnitude, direction, dot product, cross product. This page builds on that foundation algebraically. A vector space is not just a collection of arrows; it is any set that obeys a specific list of rules about addition and scaling. Understanding this abstraction is what connects your calculus, ODEs, signals, and linear algebra into a unified framework.
      </p>

      <InfoBox
        title="The 8 Axioms — What Makes Something a Vector Space"
        items={[
          "Closure under addition:         u + v is in V for all u, v in V",
          "Closure under scalar mult:      c·v is in V for all scalars c and v in V",
          "Commutativity:                  u + v = v + u",
          "Associativity:                  (u+v)+w = u+(v+w)",
          "Zero vector exists:             there is a 0 such that v + 0 = v for all v",
          "Additive inverse:               for every v there exists -v such that v + (-v) = 0",
          "Scalar identity:                1·v = v",
          "Scalar distributivity:          c(u+v) = cu + cv  and  (c+d)v = cv + dv",
        ]}
      />

      <h2 style={S.h2}>What Is a Vector Space?</h2>
      <DiagramVectorSpace />
      <Table2 rows={[
        ["Vector space",       "A set V with addition and scalar multiplication satisfying all 8 axioms\nExamples: R², R³, Rⁿ, polynomials, continuous functions, matrices"],
        ["The zero vector",    "Every vector space must contain 0\nThis is why any subspace must pass through the origin"],
        ["Closure",           "Adding or scaling any element must stay inside the set\nThis is the most commonly violated condition when checking subspaces"],
        ["Scalars",           "Scalars are typically real numbers (ℝ) in engineering contexts\nCan be complex (ℂ) in signals and controls work"],
        ["Not just geometry", "Polynomials form a vector space — you can add them and scale them\nFunctions, matrices, and signals can all be vector spaces"],
      ]} />

      <h2 style={S.h2}>Linear Independence</h2>
      <DiagramLinearIndependence />
      <Table2 rows={[
        ["Definition",            "Vectors v₁...vₙ are independent if c₁v₁+...+cₙvₙ=0 only when all cᵢ=0\nNo vector can be written as a combination of the others"],
        ["Dependence",            "If any one vector can be expressed as a combination of the rest, the set is dependent\nThe dependent vector adds no new direction — it is redundant"],
        ["Geometric intuition",   "Independent vectors point in genuinely different directions\nIn R²: two vectors are dependent if and only if they are parallel"],
        ["Testing independence",  "Put vectors as rows of a matrix and row-reduce\nIf you get a zero row, the original set was dependent"],
        ["Why it matters",        "Dependent sets are wasteful — you are carrying redundant information\nIndependence guarantees each vector contributes something new to the span"],
      ]} />

      <h2 style={S.h2}>Span</h2>
      <DiagramSpan />
      <Table2 rows={[
        ["Span definition",       "span{v₁,...,vₙ} = all vectors of the form c₁v₁ + ... + cₙvₙ\nThe complete set of destinations reachable by linear combinations"],
        ["Span always a subspace","The span of any set of vectors is always a valid subspace\nIt contains 0 (set all cᵢ=0) and is closed by construction"],
        ["1 vector in R²",        "Spans a line through the origin — one degree of freedom\nEvery point on the line is c·v for some scalar c"],
        ["2 independent in R²",   "Spans all of R² — two degrees of freedom, every point reachable\nIf the two are dependent, span is still just a line"],
        ["Spanning set",          "A set spans V if every vector in V can be written as a combination\nGoal: find the smallest spanning set — that becomes the basis"],
      ]} />

      <h2 style={S.h2}>Basis and Dimension</h2>
      <DiagramBasisDimension />
      <Table2 rows={[
        ["Basis",                 "A set of vectors that is (1) linearly independent and (2) spans V\nThe minimum number of vectors needed to describe the whole space"],
        ["Standard basis of Rⁿ", "e₁=(1,0,...,0), e₂=(0,1,...,0), ..., eₙ=(0,...,0,1)\nEvery vector v = v₁e₁ + v₂e₂ + ... + vₙeₙ — coordinates are components"],
        ["Non-standard basis",   "Any n independent vectors in Rⁿ form a valid basis\nUsed in robotics body frames, principal axes, modal analysis"],
        ["Dimension",            "dim(V) = number of vectors in any basis of V\nThis is a fixed property of V — all bases have the same size"],
        ["Coordinates",          "Once a basis is chosen, every vector has a unique coordinate representation\nChanging basis changes coordinates but not the vector itself"],
        ["Basis in engineering", "Principal axes of inertia are a basis for 3D rotation analysis\nModal shapes are a basis for vibration — every motion is a combination"],
      ]} />

      <h2 style={S.h2}>Subspaces</h2>
      <DiagramSubspaces />
      <Table2 rows={[
        ["Subspace",              "A subset W of V that is itself a vector space under the same operations\nMust contain 0, be closed under addition, and closed under scalar mult"],
        ["Quick test",            "W is a subspace iff: (1) 0 ∈ W,  (2) u+v ∈ W,  (3) cu ∈ W\nAll three must hold — failing any one disqualifies it"],
        ["Subspaces of R²",       "Only three: {0},  any line through origin,  all of R²\nDimensions 0, 1, and 2 respectively"],
        ["Subspaces of R³",       "{0},  lines through origin,  planes through origin,  all of R³\nDimensions 0, 1, 2, and 3"],
        ["Column space",          "The span of the columns of a matrix — a key subspace in linear algebra\nRepresents all outputs Ax can produce"],
        ["Null space",            "All vectors x such that Ax = 0 — always a subspace\nRepresents the directions a transformation collapses to zero"],
      ]} />

      <h2 style={S.h2}>Connection to Your Other Topics</h2>
      <Table2 rows={[
        ["→ Calculus",            "Derivatives are linear operators on the vector space of functions\nf'(af+bg) = af'+bg' — the same linearity rules apply"],
        ["→ Fourier Transforms",  "Sine and cosine functions form a basis for the space of periodic signals\nFourier decomposition = expressing a signal in that basis"],
        ["→ ODEs",                "Solutions to linear ODEs form a vector space\nGeneral solution = particular + any combination of homogeneous solutions"],
        ["→ Eigenvalues",         "Eigenvectors of a matrix form a special basis where transformation = scaling\nCentral to the next topic: Eigenvalues & Eigenvectors"],
        ["→ Statics / FEA",       "Displacement fields are vectors in a high-dimensional space\nFEA solvers find the unique combination of basis functions that satisfies equilibrium"],
        ["→ Signals & Filters",   "Signals are vectors; filters are linear operators on that space\nFrequency response = how the operator acts on each basis element (frequency)"],
      ]} />

      <InfoBox
        title="Key Takeaway — The Four Things to Check for Any Set"
        items={[
          "Does it contain the zero vector?  →  If no, it is not a subspace.",
          "Is it closed under addition?  →  Pick any two elements — does their sum stay in the set?",
          "Is it closed under scalar multiplication?  →  Scale any element — does the result stay in the set?",
          "How many independent vectors does it take to span it?  →  That number is the dimension.",
        ]}
      />
    </div>
  );
}